import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { LogOut, Plus, ShieldCheck, Trash2, X } from "lucide-react";
import { DISCHARGE_CONDITIONS } from "./parts";

export function DischargeDialog({ admission, onClose, onDone }) {
  const activeMeds = (admission.medications || []).filter((m) => m.status === "Active");
  const [meds, setMeds] = useState(activeMeds.map((m) => ({
    drug: m.drug || "", dose: m.dose || "", route: m.route || "PO",
    frequency: m.frequency || "", duration: "", quantity: "",
  })));
  const [condition, setCondition] = useState("Improved");
  const [dischargeDiagnosis, setDischargeDiagnosis] = useState(admission.admitting_diagnosis || "");
  const [treatmentGiven, setTreatmentGiven] = useState("");
  const [summary, setSummary] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpNotes, setFollowUpNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const updateMed = (i, patch) => setMeds((prev) => prev.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const removeMed = (i) => setMeds((prev) => prev.filter((_, idx) => idx !== i));
  const addMed = () => setMeds((prev) => [...prev, { drug: "", dose: "", route: "PO", frequency: "", duration: "", quantity: "" }]);

  const submit = async (e) => {
    e.preventDefault();
    if (!dischargeDiagnosis.trim() || !summary.trim()) { toast.error("Discharge diagnosis and summary are required"); return; }
    const clean = meds.filter((m) => (m.drug || "").trim().length > 0);
    for (const m of clean) {
      if (!m.dose.trim() || !m.frequency.trim()) { toast.error(`Med '${m.drug}' needs dose and frequency`); return; }
    }
    try {
      setSaving(true);
      await api.post(`/admissions/${admission.admission_id}/discharge`, {
        condition,
        discharge_diagnosis: dischargeDiagnosis.trim(),
        treatment_given: treatmentGiven.trim() || null,
        summary: summary.trim(),
        follow_up_date: followUpDate || null,
        follow_up_notes: followUpNotes.trim() || null,
        discharge_meds: clean,
      });
      toast.success("Patient discharged. Summary signed and locked.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to discharge");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4 ths-print-hide" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-3xl shadow-xl border border-slate-200 max-h-[92vh] overflow-y-auto" data-testid="discharge-dialog">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <div className="font-semibold flex items-center gap-2"><LogOut className="w-4 h-4 text-[#1976D2]" /> Discharge patient</div>
            <div className="text-xs text-slate-500 mt-0.5">{admission.patient_name} · {admission.patient_ths_id} · {admission.admission_no}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-800">Discharge medications</div>
              <button type="button" data-testid="disc-add-med" onClick={addMed} className="h-7 px-2.5 rounded-lg text-xs font-medium border border-slate-200 hover:bg-slate-50 flex items-center gap-1"><Plus className="w-3 h-3" /> Add row</button>
            </div>
            {meds.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500">No discharge medications. Click “Add row” to add one.</div>
            ) : (
              <div className="space-y-2">
                {meds.map((m, i) => (
                  <div key={i} data-testid={`disc-med-row-${i}`} className="grid grid-cols-12 gap-2 items-start">
                    <input data-testid={`disc-med-drug-${i}`} value={m.drug} onChange={(e) => updateMed(i, { drug: e.target.value })} placeholder="Drug" className="col-span-3 h-9 px-2 rounded-lg border border-slate-200 text-sm" />
                    <input data-testid={`disc-med-dose-${i}`} value={m.dose} onChange={(e) => updateMed(i, { dose: e.target.value })} placeholder="Dose" className="col-span-2 h-9 px-2 rounded-lg border border-slate-200 text-sm" />
                    <select data-testid={`disc-med-route-${i}`} value={m.route} onChange={(e) => updateMed(i, { route: e.target.value })} className="col-span-1 h-9 px-1 rounded-lg border border-slate-200 text-sm bg-white">
                      {["PO","IV","IM","SC","PR","Topical","Inhaled"].map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <input data-testid={`disc-med-freq-${i}`} value={m.frequency} onChange={(e) => updateMed(i, { frequency: e.target.value })} placeholder="Frequency" className="col-span-2 h-9 px-2 rounded-lg border border-slate-200 text-sm" />
                    <input data-testid={`disc-med-duration-${i}`} value={m.duration} onChange={(e) => updateMed(i, { duration: e.target.value })} placeholder="Duration" className="col-span-2 h-9 px-2 rounded-lg border border-slate-200 text-sm" />
                    <input data-testid={`disc-med-qty-${i}`} value={m.quantity} onChange={(e) => updateMed(i, { quantity: e.target.value })} placeholder="Qty" className="col-span-1 h-9 px-2 rounded-lg border border-slate-200 text-sm" />
                    <button type="button" data-testid={`disc-med-remove-${i}`} onClick={() => removeMed(i)} className="col-span-1 h-9 rounded-lg text-slate-400 hover:text-[#B71C1C] hover:bg-red-50 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500 font-medium">Condition at discharge *</label>
              <select data-testid="disc-condition" value={condition} onChange={(e) => setCondition(e.target.value)} className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                {DISCHARGE_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 font-medium">Review / follow-up date</label>
              <input type="date" data-testid="disc-follow-date" value={followUpDate} onChange={(e) => setFollowUpDate(e.target.value)} className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-500 font-medium">Discharge diagnosis *</label>
            <input data-testid="disc-diagnosis" value={dischargeDiagnosis} onChange={(e) => setDischargeDiagnosis(e.target.value)} placeholder="Final diagnosis at discharge" className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm" />
          </div>

          <div>
            <label className="text-xs text-slate-500 font-medium">Treatment given</label>
            <textarea data-testid="disc-treatment" value={treatmentGiven} onChange={(e) => setTreatmentGiven(e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="Summary of the treatment provided during admission" />
          </div>

          <div>
            <label className="text-xs text-slate-500 font-medium">Discharge summary &amp; follow-up instructions *</label>
            <textarea data-testid="disc-summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={4} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="Patient progress, condition, and follow-up instructions" />
          </div>

          <div>
            <label className="text-xs text-slate-500 font-medium">Follow-up notes</label>
            <textarea data-testid="disc-follow-notes" value={followUpNotes} onChange={(e) => setFollowUpNotes(e.target.value)} rows={2} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" placeholder="Where to review, any specific tests to repeat, etc." />
          </div>

          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-[#1B5E20] mt-0.5" />
            <div>Submitting will <strong>sign &amp; lock</strong> this discharge summary under your name. Later corrections must be added as an addendum. The admission will move out of the active Admitted list.</div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50 rounded-b-2xl sticky bottom-0">
          <button type="button" onClick={onClose} className="h-9 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-100">Cancel</button>
          <button data-testid="disc-submit" type="submit" disabled={saving} className="h-9 px-4 rounded-lg bg-[#B71C1C] text-white text-sm font-medium hover:bg-[#8f1414] disabled:opacity-60 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4" /> {saving ? "Signing…" : "Sign & discharge"}
          </button>
        </div>
      </form>
    </div>
  );
}
